import { CheckOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { AccordionAPI } from '@mm/components/src/AccordionItem/AccordionItem';
import { landmineNames } from '@mm/constants/landmine-names';
import { KlaviyoIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectKlaviyoConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { useFetchConnections } from '../../../use-fetch-connections';
import { KlaviyoSettings } from './KlaviyoSettings';
import { useConnectOauthToken } from './use-connect-oauth-token';

export type KlaviyoSetupMode = 'oauth' | 'api_key' | null;

const connectionSetupHeader = (onClick: () => void) => (
  <Button
    css={{ container: 'integrations__header-add-button-secondary' }}
    onClick={e => {
      e?.stopPropagation();
      onClick();
    }}
    color={ButtonColor.secondaryBlue}
  >
    Setup Your Connection
  </Button>
);

const modeHeader = (
  mode: KlaviyoSetupMode,
  setMode: (mode: KlaviyoSetupMode) => void,
  connection: ConnectionModel | undefined,
) => {
  const action = mode === 'api_key' ? 'oauth' : 'api_key';
  let connectButton: string | null = null;
  let configuredButton: string | null = null;
  const connectionPatterns = {
    apiKey: {
      isConnected: !!connection?.klaviyoAccessToken,
      configuredButton: 'Connected with API Key',
      shouldConnectDirectly:
        !connection?.klaviyoAccessToken ||
        connection?.klaviyoAccessToken?.status === 'error',
      connectButton: 'Connect Directly',
    },
    oauth: {
      isConnected: !!connection?.klaviyoOauthToken,
      configuredButton: 'Connected Directly',
      shouldConnectWithApiKey:
        !connection?.klaviyoOauthToken ||
        !!connection?.klaviyoOauthToken?.errorInfo,
      connectButton: 'Connect with API Key',
    },
  };

  const apiConfiguredPattern = connectionPatterns.apiKey;
  const oauthConfiguredPattern = connectionPatterns.oauth;

  if (mode === 'api_key') {
    if (apiConfiguredPattern.isConnected) {
      configuredButton = apiConfiguredPattern.configuredButton;
    }

    if (apiConfiguredPattern.shouldConnectDirectly) {
      connectButton = apiConfiguredPattern.connectButton;
    }
  }

  if (mode === 'oauth') {
    if (oauthConfiguredPattern.isConnected) {
      configuredButton = oauthConfiguredPattern.configuredButton;
    }

    if (oauthConfiguredPattern.shouldConnectWithApiKey) {
      connectButton = oauthConfiguredPattern.connectButton;
    }
  }
  return (
    <div className="integrations__header-actions">
      {connectButton ? (
        <Button
          css={{ container: 'integrations__header-add-button-secondary' }}
          type="button"
          onClick={e => {
            e?.stopPropagation();
            setMode(action);
          }}
          color={ButtonColor.secondaryBlue}
        >
          {connectButton}
        </Button>
      ) : null}
      {configuredButton ? (
        <Button
          css={{ container: 'integrations__header-add-button-secondary' }}
          type="button"
          disabled
          leftIcon={<CheckOutlined />}
          color={ButtonColor.secondaryBlue}
        >
          {configuredButton}
        </Button>
      ) : null}
    </div>
  );
};

export const KlaviyoIntegrations = () => {
  const { fetch } = useFetchConnections();
  const { retrieveById } = useConnectionCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const { getLists } = useKlaviyoCRUDActions();
  const forceGetLists = (webhookId: number) => getLists(webhookId, true);
  const { openNewBillingModal2023 } = useModalActions();

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.KLAVIYO_UPGRADE,
      onSuccessBilling,
    });
  };
  const connectOauthToken = useConnectOauthToken(
    'klaviyoConnect',
    'klaviyoTokenReceived',
    () => {
      if (klaviyoConnection) {
        retrieveById(activeFbPageId, klaviyoConnection.id);
        forceGetLists(klaviyoConnection.id);
      }
    },
  );
  const connectToken = async () =>
    connectOauthToken(
      activeFbPageId,
      !!hasNativeIntegrationsFeature,
      showUpgradeModal,
      klaviyoConnection,
      {
        name: 'Klaviyo Connection',
        connectionKind: 'klaviyo',
      },
    );

  const sectionProps = {
    landmineName: landmineNames.KLAVIYO_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };

  const [mode, setMode] = useState<KlaviyoSetupMode>(null);

  const klaviyoConnections = useSelector(selectKlaviyoConnections);
  const klaviyoConnection = klaviyoConnections?.[0];

  useEffect(() => {
    if (klaviyoConnection && mode === null) {
      if (klaviyoConnection.klaviyoAccessToken) {
        setMode('api_key');
      } else if (klaviyoConnection.klaviyoOauthToken) {
        setMode('oauth');
      }
    }
  }, [klaviyoConnection, mode]);

  useEffect(() => {
    fetch(1, 200, 'klaviyo');
  }, [fetch]);

  const ref = useRef<AccordionAPI>(null);

  const header =
    mode === null
      ? connectionSetupHeader(connectToken)
      : modeHeader(
          mode,
          (mode: KlaviyoSetupMode) => {
            ref.current?.open();
            setMode(mode);
          },
          klaviyoConnection,
        );

  return (
    <BaseIntegration
      accordionRef={ref}
      titleComponent={
        <BaseIntegrationHeader
          title="Klaviyo"
          upgradeButtonName="Set up your connection"
          subtitle="Automatically sync your Customers.ai contacts to Klaviyo"
          icon={<KlaviyoIcon />}
          headerRightComponent={header}
          css={{ icon: 'integrations__klaviyo-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="klaviyo"
    >
      <KlaviyoSettings
        onSelectMode={mode => setMode(mode)}
        mode={mode}
        showUpgradeModal={showUpgradeModal}
        hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
      />
    </BaseIntegration>
  );
};
